import { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";

type Pattern = "box" | "coherent" | "478";

const PHASES: Record<Pattern, Array<{ label: string; seconds: number }>> = {
  box: [
    { label: "Inhale", seconds: 4 },
    { label: "Hold", seconds: 4 },
    { label: "Exhale", seconds: 4 },
    { label: "Hold", seconds: 4 },
  ],
  coherent: [
    { label: "Inhale", seconds: 5.5 },
    { label: "Exhale", seconds: 5.5 },
  ],
  "478": [
    { label: "Inhale", seconds: 4 },
    { label: "Hold", seconds: 7 },
    { label: "Exhale", seconds: 8 },
  ],
};

export function BreathMode({ pattern, onClose }: { pattern: Pattern; onClose: () => void }) {
  const phases = PHASES[pattern];
  const [idx, setIdx] = useState(0);
  const [t, setT] = useState(0);
  const startRef = useRef(Date.now());

  useEffect(() => {
    const iv = setInterval(() => {
      const elapsed = (Date.now() - startRef.current) / 1000;
      const dur = phases[idx].seconds;
      if (elapsed >= dur) {
        setIdx((i) => (i + 1) % phases.length);
        startRef.current = Date.now();
        setT(0);
      } else setT(elapsed);
    }, 50);
    return () => clearInterval(iv);
  }, [idx, phases]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const phase = phases[idx];
  const progress = Math.min(1, t / phase.seconds);
  const scale = useMemo(() => {
    if (phase.label === "Inhale") return 0.5 + progress * 0.5;
    if (phase.label === "Exhale") return 1 - progress * 0.5;
    return phase.label === "Hold" ? (idx === 1 ? 1 : 0.5) : 0.75;
  }, [phase.label, progress, idx]);

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-obsidian/95 backdrop-blur-lg">
      <button onClick={onClose} aria-label="Close breath mode"
        className="absolute right-5 top-5 inline-flex h-11 w-11 items-center justify-center rounded-full border hairline text-ivory/80 hover:border-gold hover:text-gold">
        <X size={18} />
      </button>
      <div className="relative flex h-[70vmin] w-[70vmin] items-center justify-center">
        <div
          className="absolute inset-0 rounded-full border-gold border transition-transform duration-100 ease-linear"
          style={{ transform: `scale(${scale})`, boxShadow: "0 0 120px rgba(217,184,114,0.35)", background: "radial-gradient(circle, rgba(217,184,114,0.15), rgba(0,0,0,0))" }}
        />
        <div className="relative z-10 text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-gold">{pattern.toUpperCase()}</p>
          <p className="mt-3 font-serif text-5xl text-ivory sm:text-6xl">{phase.label}</p>
          <p className="mt-2 text-sm text-ivory/60">{Math.ceil(phase.seconds - t)}s</p>
        </div>
      </div>
      <p className="mt-8 text-xs uppercase tracking-[0.3em] text-ivory/50">Press ESC to exit</p>
    </div>
  );
}
